// "View as customer": a permitted VisionHeight admin sees the app exactly as one
// customer sees it, and can change nothing.
//
// Two facts ride in the Descope session token, and this file is the only place
// that reads them:
//   • `permissions` — may THIS user start a view-as? Decides what RENDERS; the
//     server re-checks it on every /api/view-as/* call.
//   • `act.sub`     — is THIS session a view-as? Drives the banner and the
//     read-only affordances; the server enforces read-only off the same claim
//     (auth/web_auth.py), so nothing here is load-bearing for security.
//
// ⚠ Not to be confused with `features/impersonation` next door — that screen is
// brand impersonation (lookalike domains) and has nothing to do with this.
import { parsePermissions } from '@/features/auth/permissions';

/** The Descope RBAC permission that unlocks view-as. Granted by hand in the
 *  Descope console, which is why there is no list of people in this codebase.
 *  Mirrors `VIEW_AS_PERMISSION` in the server's main.py — same spelling. */
export const VIEW_AS_PERMISSION = 'Impersonate';

/**
 * The claims inside a session JWT, or null if there is no readable token.
 *
 * Both facts below come from HERE rather than from the SDK's `claims` value,
 * which is not the session token's payload: the SDK republishes whatever the
 * last auth response carried, and on this project that is the REFRESH token —
 * which has `account`, `allowedModels` and `insightsEnabled` (so those features
 * work by luck) but carries no `permissions`, `roles` or `tenants` at all.
 * Reading the session token directly is what makes this independent of which
 * response happened to arrive last. Verified against staging.
 *
 * Signature-checking is not this function's job and never could be: the token
 * is verified server-side on every request, and everything read here only
 * decides what to draw.
 */
export function decodeJwtClaims(token: string | undefined | null): Record<string, unknown> | null {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    // base64url → base64, then restore the padding the URL form drops.
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '='));
    const claims: unknown = JSON.parse(json);
    return claims && typeof claims === 'object' ? (claims as Record<string, unknown>) : null;
  } catch {
    // A malformed token is a signed-out-shaped state, not a crash on boot.
    return null;
  }
}

/**
 * May this user open the view-as picker?
 *
 * Off unless the permission is actually in the claim, so a user nobody has
 * granted it never sees the menu item. The both-levels claim walking (project
 * and tenant-scoped — the console's per-user dialog only grants the tenant
 * kind) started life here and now lives in `auth/permissions.ts`, shared with
 * the tenant-facing RBAC permissions.
 */
export function parseCanViewAs(claims: unknown): boolean {
  return parsePermissions(claims).has(VIEW_AS_PERMISSION);
}

/**
 * Is this session looking at a customer right now?
 *
 * `act` is the standard actor claim (RFC 8693): `sub` is the customer, `act.sub`
 * is the admin behind them. An actor without a subject names nobody, so it does
 * not count — this decides whether the banner appears, and a view-as session
 * with no banner is the one thing this feature must never produce.
 */
export function parseViewingAs(claims: unknown): boolean {
  const act = (claims as { act?: { sub?: unknown } } | null | undefined)?.act;
  return typeof act?.sub === 'string' && act.sub.length > 0;
}
